/* eslint-disable css-modules/no-undef-class */
import React from 'react';
import { GetServerSideProps, NextPage } from 'next';
import Link from 'next/link';
import { DESIGNER } from 'routes';

import { getDesignerList } from 'api';
import { NotFoundError } from 'api/utils';
import Block from 'components/Block';
import Container from 'components/Container';
import DesignerCard from 'components/DesignerCard';
import Grid from 'components/Grid';
import MetaTitle from 'components/MetaTitle';
import Spaced from 'components/Spaced';
import Typography from 'components/Typography';
import Layout from 'containers/Layout';
import Main from 'containers/Main';
import styles from 'styles/pages/Designers.module.css';
import { DesignerDetail } from 'types/Designer';
import { chunkBy, cx } from 'utils';

const sortDesigners = (designers: DesignerDetail[]) => {
  // Sort aphabetically
  const sortedDesigners = designers.sort((a, b) => {
    if (a.lastname < b.lastname) {
      return -1;
    }
    if (a.lastname > b.lastname) {
      return 1;
    }
    return 0;
  });

  const designersLetterMap: Record<string, DesignerDetail[]> = {};

  // Append designer to first letter of lastname
  sortedDesigners.forEach(designer => {
    const lastNameFirstLetter = designer.lastname.substring(0, 1).toUpperCase();

    if (!designersLetterMap[lastNameFirstLetter]) {
      designersLetterMap[lastNameFirstLetter] = [];
    }

    designersLetterMap[lastNameFirstLetter].push(designer);
  });

  // Make designers iterable
  const designerEntries = Object.entries(designersLetterMap);

  return { designerEntries, sortedDesigners };
};

const makeDesignerLinkProps = (designer: DesignerDetail) => ({
  passHref: true,
  href: {
    pathname: DESIGNER.pathname,
    query: { slug: designer.slug },
  },
});

const DesignerList: NextPage<{ designers: DesignerDetail[] }> = ({ designers }) => {
  const { designerEntries, sortedDesigners } = sortDesigners(designers);

  const featuredSortedDesigners = sortedDesigners.filter(d => d.is_featured);

  return (
    <>
      <MetaTitle>Designers</MetaTitle>
      <Layout>
        <Main paddingTop>
          <Container>
            <Spaced className={styles.hideOnMobile}>
              <Typography block>FEATURED</Typography>
              <div className={cx(styles.block, styles.featuredDesignersBlock)}>
                <Grid columns={4}>
                  {featuredSortedDesigners.map(designer => (
                    <Link key={designer.id} {...makeDesignerLinkProps(designer)}>
                      <DesignerCard designer={designer} key={designer.id} />
                    </Link>
                  ))}
                </Grid>
              </div>
            </Spaced>

            <div className={styles.allDesigners}>
              {designerEntries.map(([letter, designers], index) => {
                const chunkedDesigners = chunkBy(designers, Math.ceil(designers.length / 2));

                return (
                  <Block
                    id={letter}
                    className={cx(styles.block, styles.allDesignersBlock)}
                    key={index}
                  >
                    <div className={styles.letterContainer}>
                      <Typography className={styles.textStyle}>{letter}</Typography>
                    </div>
                    <div className={styles.chunkContainer}>
                      {chunkedDesigners.map((_designers, iindex) => {
                        return (
                          <div className={styles.chunk} key={iindex}>
                            {_designers.map(designer => (
                              <Link
                                {...makeDesignerLinkProps(designer)}
                                key={designer.id}
                                legacyBehavior
                              >
                                <Typography
                                  uppercase
                                  component="a"
                                  className={styles.textStyle}
                                >{`${designer.firstname} ${designer.lastname}`}</Typography>
                              </Link>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </Block>
                );
              })}
            </div>
          </Container>
        </Main>
      </Layout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async () => {
  try {
    const { data: designers } = await getDesignerList();

    return {
      props: {
        designers,
      },
    };
  } catch (error) {
    if (error instanceof NotFoundError) {
      return {
        props: {
          designers: [],
        },
      };
    } else {
      throw error;
    }
  }
};

export default DesignerList;
